import { useMemo, useState } from 'react';
import { IconChevronDown, IconChevronRight } from '../../components/icons';
import { Badge } from '../../components/ui';
import type { Scope } from '../../lib/auth';

/**
 * Wiederverwendbare Rechtematrix.
 *
 * Jede Zeile entspricht einem Recht und lässt sich unabhängig auf
 * "nicht gesetzt", "erlauben" oder "verweigern" stellen – jeweils mit einem
 * eigenen Datensicht-Scope. Das ist die Grundlage für die detaillierte
 * Anpassbarkeit von Rollen, Gruppen und Einzelbenutzern.
 */

export interface Grant {
  permissionKey: string;
  effect: 'allow' | 'deny';
  scope: Scope;
}

export interface PermissionDef {
  key: string;
  module: string;
  action: string;
  label: string;
  description?: string;
  scopable?: boolean;
}

export interface ModuleDef {
  key: string;
  label: string;
  description: string;
}

export const SCOPE_OPTIONS: { key: Scope; label: string }[] = [
  { key: 'own', label: 'Nur eigene' },
  { key: 'assigned', label: 'Eigene + zugewiesene' },
  { key: 'objects', label: 'Meine Objekte' },
  { key: 'tenant', label: 'Gesamter Betrieb' },
  { key: 'all', label: 'Mandantenübergreifend' },
];

export function PermissionMatrix({
  catalog,
  modules,
  grants,
  onChange,
  readOnly,
  inherited,
}: {
  catalog: PermissionDef[];
  modules: ModuleDef[];
  grants: Grant[];
  onChange: (grants: Grant[]) => void;
  readOnly?: boolean;
  /** Rechte, die bereits über Rollen oder übergeordnete Gruppen wirken. */
  inherited?: Record<string, { effect: string; scope: string; source: string }>;
}) {
  const [open, setOpen] = useState<string[]>(modules.slice(0, 2).map((module) => module.key));
  const [filter, setFilter] = useState('');

  const byKey = useMemo(() => new Map(grants.map((grant) => [grant.permissionKey, grant])), [grants]);

  const setGrant = (key: string, effect: 'allow' | 'deny' | null, scope?: Scope) => {
    if (readOnly) return;
    const next = grants.filter((grant) => grant.permissionKey !== key);
    if (effect) {
      next.push({
        permissionKey: key,
        effect,
        scope: scope ?? byKey.get(key)?.scope ?? 'tenant',
      });
    }
    onChange(next);
  };

  const toggleModule = (moduleKey: string) =>
    setOpen((current) =>
      current.includes(moduleKey) ? current.filter((key) => key !== moduleKey) : [...current, moduleKey],
    );

  const setModuleAll = (moduleKey: string, effect: 'allow' | 'deny' | null) => {
    if (readOnly) return;
    const keys = catalog.filter((permission) => permission.module === moduleKey).map((p) => p.key);
    const next = grants.filter((grant) => !keys.includes(grant.permissionKey));
    if (effect) {
      for (const key of keys) {
        next.push({ permissionKey: key, effect, scope: byKey.get(key)?.scope ?? 'tenant' });
      }
    }
    onChange(next);
  };

  const visible = filter.trim()
    ? catalog.filter(
        (permission) =>
          permission.label.toLowerCase().includes(filter.toLowerCase()) ||
          permission.key.toLowerCase().includes(filter.toLowerCase()),
      )
    : catalog;

  return (
    <div className="stack">
      <div className="row between">
        <input
          type="search"
          placeholder="Recht suchen …"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          style={{ maxWidth: 300 }}
        />
        <span className="small subtle">
          {grants.filter((grant) => grant.effect === 'allow').length} erlaubt ·{' '}
          {grants.filter((grant) => grant.effect === 'deny').length} verweigert
        </span>
      </div>

      {modules.map((module) => {
        const permissions = visible.filter((permission) => permission.module === module.key);
        if (permissions.length === 0) return null;
        const expanded = open.includes(module.key) || !!filter.trim();
        const allowed = permissions.filter((p) => byKey.get(p.key)?.effect === 'allow').length;
        const denied = permissions.filter((p) => byKey.get(p.key)?.effect === 'deny').length;

        return (
          <div className="permission-module" key={module.key}>
            <div className="permission-module-header" onClick={() => toggleModule(module.key)}>
              {expanded ? <IconChevronDown width={15} height={15} /> : <IconChevronRight width={15} height={15} />}
              <div style={{ flex: 1 }}>
                <strong>{module.label}</strong>
                <p>{module.description}</p>
              </div>
              {allowed > 0 && <Badge variant="success">{allowed} erlaubt</Badge>}
              {denied > 0 && <Badge variant="danger">{denied} verweigert</Badge>}
              {!readOnly && (
                <div className="row" style={{ gap: 4 }} onClick={(event) => event.stopPropagation()}>
                  <button className="btn sm ghost" onClick={() => setModuleAll(module.key, 'allow')}>
                    alle
                  </button>
                  <button className="btn sm ghost" onClick={() => setModuleAll(module.key, null)}>
                    keine
                  </button>
                </div>
              )}
            </div>

            {expanded &&
              permissions.map((permission) => {
                const grant = byKey.get(permission.key);
                const inheritedGrant = inherited?.[permission.key];
                return (
                  <div className="permission-row" key={permission.key}>
                    <div>
                      <div className="permission-label">{permission.label}</div>
                      <div className="permission-key">{permission.key}</div>
                      {permission.description && <div className="permission-desc">{permission.description}</div>}
                      {inheritedGrant && !grant && (
                        <div className="inherited-note">
                          ↳ bereits {inheritedGrant.effect === 'allow' ? 'erlaubt' : 'verweigert'} über{' '}
                          <strong>{inheritedGrant.source}</strong>
                        </div>
                      )}
                    </div>

                    <div className="effect-toggle">
                      <button
                        className={!grant ? 'active-none' : ''}
                        disabled={readOnly}
                        onClick={() => setGrant(permission.key, null)}
                        title="Nicht gesetzt – es gelten geerbte Rechte"
                      >
                        –
                      </button>
                      <button
                        className={grant?.effect === 'allow' ? 'active-allow' : ''}
                        disabled={readOnly}
                        onClick={() => setGrant(permission.key, 'allow')}
                      >
                        Erlauben
                      </button>
                      <button
                        className={grant?.effect === 'deny' ? 'active-deny' : ''}
                        disabled={readOnly}
                        onClick={() => setGrant(permission.key, 'deny')}
                      >
                        Verweigern
                      </button>
                    </div>

                    <div>
                      {grant?.effect === 'allow' && permission.scopable !== false ? (
                        <select
                          value={grant.scope}
                          disabled={readOnly}
                          onChange={(event) => setGrant(permission.key, 'allow', event.target.value as Scope)}
                        >
                          {SCOPE_OPTIONS.map((option) => (
                            <option key={option.key} value={option.key}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : grant?.effect === 'allow' ? (
                        <span className="small subtle">ohne Datensicht-Einschränkung</span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Nur-Lese-Darstellung der effektiven Rechte inklusive Herkunft.
 */
export function PermissionExplanation({
  catalog,
  modules,
  effective,
  explanation,
}: {
  catalog: PermissionDef[];
  modules: ModuleDef[];
  effective: Record<string, { effect: string; scope: string }>;
  explanation: Record<string, { effective: any; grants: any[] }>;
}) {
  const [showAll, setShowAll] = useState(false);

  return (
    <div className="stack">
      <div className="row between">
        <span className="small muted">
          {Object.values(effective).filter((entry) => entry.effect === 'allow').length} von {catalog.length} Rechten
          aktiv
        </span>
        <label className="checkbox-row">
          <input type="checkbox" checked={showAll} onChange={(event) => setShowAll(event.target.checked)} />
          <span className="small">Auch fehlende Rechte anzeigen</span>
        </label>
      </div>

      {modules.map((module) => {
        const permissions = catalog.filter(
          (permission) => permission.module === module.key && (showAll || effective[permission.key]),
        );
        if (!permissions.length) return null;

        return (
          <div className="permission-module" key={module.key}>
            <div className="permission-module-header" style={{ cursor: 'default' }}>
              <div style={{ flex: 1 }}>
                <strong>{module.label}</strong>
                <p>{module.description}</p>
              </div>
            </div>
            {permissions.map((permission) => {
              const entry = effective[permission.key];
              const grants = explanation[permission.key]?.grants ?? [];
              return (
                <div className="permission-row" key={permission.key}>
                  <div>
                    <div className="permission-label">{permission.label}</div>
                    <div className="permission-key">{permission.key}</div>
                  </div>
                  <div>
                    {entry ? (
                      <Badge variant={entry.effect === 'allow' ? 'success' : 'danger'}>
                        {entry.effect === 'allow' ? 'Erlaubt' : 'Verweigert'}
                      </Badge>
                    ) : (
                      <span className="small subtle">nicht vergeben</span>
                    )}
                  </div>
                  <div className="small subtle">
                    {entry && (
                      <>
                        <div>{scopeLabel(entry.scope)}</div>
                        {grants.length > 0 && (
                          <div className="truncate" title={grants.map(describeGrant).join('\n')}>
                            aus: {grants.map(describeGrant).join(', ')}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export function scopeLabel(scope: string): string {
  return SCOPE_OPTIONS.find((option) => option.key === scope)?.label ?? scope;
}

function describeGrant(grant: any): string {
  const prefix = grant.viaGroupLabel ? `${grant.viaGroupLabel} → ` : '';
  return `${prefix}${grant.sourceLabel}${grant.effect === 'deny' ? ' (Deny)' : ''}`;
}
